import throttle from "lodash/throttle";

class GoogleMapsStatic extends HTMLElement {
    static get observedAttributes() {
        return ["lat", "lng", "zoom", "key", "show-marker"];
    }

    constructor() {
        super();

        this.gmapSettings = null;
        this.imageEl = null;
        this.mapStyle = [
            {
                featureType: "all",
                elementType: "labels",
                stylers: [
                    {
                        visibility: "on",
                    },
                ],
            },
            {
                featureType: "all",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        saturation: 36,
                    },
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 40,
                    },
                ],
            },
            {
                featureType: "all",
                elementType: "labels.text.stroke",
                stylers: [
                    {
                        visibility: "on",
                    },
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 16,
                    },
                ],
            },
            {
                featureType: "all",
                elementType: "labels.icon",
                stylers: [
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                featureType: "administrative",
                elementType: "geometry.fill",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 20,
                    },
                ],
            },
            {
                featureType: "administrative",
                elementType: "geometry.stroke",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 17,
                    },
                    {
                        weight: 1.2,
                    },
                ],
            },
            {
                featureType: "administrative.country",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#e5c163",
                    },
                ],
            },
            {
                featureType: "administrative.locality",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#c4c4c4",
                    },
                ],
            },
            {
                featureType: "administrative.neighborhood",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#e5c163",
                    },
                ],
            },
            {
                featureType: "landscape",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 20,
                    },
                ],
            },
            {
                featureType: "poi",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 21,
                    },
                    {
                        visibility: "on",
                    },
                ],
            },
            {
                featureType: "poi.business",
                elementType: "geometry",
                stylers: [
                    {
                        visibility: "on",
                    },
                ],
            },
            {
                featureType: "road.highway",
                elementType: "geometry.fill",
                stylers: [
                    {
                        color: "#e5c163",
                    },
                    {
                        lightness: "0",
                    },
                ],
            },
            {
                featureType: "road.highway",
                elementType: "geometry.stroke",
                stylers: [
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                featureType: "road.highway",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#ffffff",
                    },
                ],
            },
            {
                featureType: "road.highway",
                elementType: "labels.text.stroke",
                stylers: [
                    {
                        color: "#e5c163",
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 18,
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "geometry.fill",
                stylers: [
                    {
                        color: "#575757",
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#ffffff",
                    },
                ],
            },
            {
                featureType: "road.arterial",
                elementType: "labels.text.stroke",
                stylers: [
                    {
                        color: "#2c2c2c",
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 16,
                    },
                ],
            },
            {
                featureType: "road.local",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#999999",
                    },
                ],
            },
            {
                featureType: "transit",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 19,
                    },
                ],
            },
            {
                featureType: "water",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#000000",
                    },
                    {
                        lightness: 17,
                    },
                ],
            },
        ];
    }

    connectedCallback() {
        this.lat = this.getAttribute("lat");
        this.lng = this.getAttribute("lng");
        this.zoom = this.getAttribute("zoom") ?? 14;
        this.key = this.getAttribute("key");
        this.showMarker = this.getAttribute("show-marker") ?? true;
        this.markerIconUrl = this.getAttribute("marker-icon-url");

        if (!this.lat || !this.lng || !this.key) {
            console.error("[google-maps-static]: Missing required attributes");
            return;
        }

        this.init();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue) {
            this[name] = newValue;

            this.generateStaticMap();
        }
    }

    init() {
        this.createImageElement();

        this.generateStaticMap();

        window.addEventListener(
            "resize",
            throttle(this.generateStaticMap.bind(this), 200),
        );
    }

    createImageElement() {
        this.imageEl = document.createElement("img");
        this.imageEl.loading = "lazy";
        this.appendChild(this.imageEl);
    }

    displayMapImage() {
        this.imageEl.src = this.mapUrl;
    }

    generateStaticMap() {
        if (this.imageEl === null) {
            return;
        }

        const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";

        let { width, height } = this.imageEl.getBoundingClientRect();
        const aspectRatio = width / height;

        if (width > 640) {
            width = 640;
            height = width / aspectRatio;
        }

        const params = {
            center: `${this.lat},${this.lng}`,
            zoom: this.zoom,
            key: this.key,
            scale: 2,
        };

        if (this.showMarker) {
            params.markers = `color:0xF60E5F|size:mid`;

            // if (this.markerIconUrl) {
            //     params.markers += `|icon:${this.markerIconUrl}`;
            // }

            params.markers += `|${this.lat},${this.lng}`;
        }

        params.size = `${Math.round(width)}x${Math.round(height)}`;

        const queryString = Object.keys(params)
            .map((key) => key + "=" + encodeURIComponent(params[key]))
            .join("&");

        /* eslint-disable-next-line */
        const customStyle = `&${this.getStyleArray(this.mapStyle)}`;

        const mapUrl = `${baseUrl}?${queryString}${customStyle}`;

        this.mapUrl = mapUrl;
        this.displayMapImage();
    }

    getStyleArray(styles) {
        var result = [];
        styles.forEach(function (v, i, a) {
            var style = "";
            if (v.stylers.length > 0) {
                // Needs to have a style rule to be valid.
                style +=
                    (v.hasOwnProperty("featureType")
                        ? "feature:" + v.featureType
                        : "feature:all") + "|";
                style +=
                    (v.hasOwnProperty("elementType")
                        ? "element:" + v.elementType
                        : "element:all") + "|";
                v.stylers.forEach(function (val, i, a) {
                    var propertyname = Object.keys(val)[0];
                    var propertyval = val[propertyname]
                        .toString()
                        .replace("#", "0x");
                    style += propertyname + ":" + propertyval + "|";
                });
            }
            result.push("style=" + encodeURIComponent(style));
        });

        return result.join("&");
    }
}

customElements.define("google-maps-static", GoogleMapsStatic);
